import { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Globe, FileText, ShieldCheck, BarChart3, ChevronRight, AlertTriangle } from 'lucide-react';
import {
  Factory, ShoppingBag, Car, HeartPulse, Building2, Truck,
  Flame, Shield, UtensilsCrossed, Plane, Gem, HardHat
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

const INDUSTRY_DATA = {
  manufacturing: {
    name: 'Manufacturing',
    icon: Factory,
    color: '#0066CC',
    hero: 'Tax compliance built for complex, multi-country manufacturing operations.',
    subtitle: 'From raw materials to finished goods across dozens of jurisdictions — Taxera automates the e-Invoicing, SAF-T, and VAT obligations that manufacturing companies cannot afford to get wrong.',
    challenges: [
      { title: 'Multi-country supply chains', desc: 'Inbound and outbound invoicing across 40+ countries with different e-Invoicing mandates, formats (UBL, CII, FatturaPA), and real-time reporting requirements.' },
      { title: 'High transaction volumes', desc: 'Hundreds of thousands of invoices per month — each requiring format validation, digital signatures, and submission to different tax authorities.' },
      { title: 'SAP landscape complexity', desc: 'Multiple SAP instances, factory-specific configurations, and legacy ECC systems migrating to S/4HANA — all requiring consistent tax compliance.' },
      { title: 'Customs & excise overlap', desc: 'Cross-border movements, bonded warehouses, and intra-community transactions create overlapping VAT, customs, and excise obligations.' },
    ],
    taxeraValue: [
      { title: 'Middleware-native ERP integration', desc: 'Sits inside your SAP landscape, not outside it. No batch uploads, no file exports — real-time data flow from production orders to tax authority submission.' },
      { title: 'Country-mandate automation', desc: 'Pre-built support for every e-Invoicing and SAF-T mandate your factories face: Italy SDI, Poland KSeF, Romania RO e-Factura, Greece myDATA, and dozens more.' },
      { title: 'Multi-entity consolidation', desc: 'One platform for every legal entity, every country, every obligation. Unified dashboards, consolidated reporting, single audit trail.' },
      { title: '8-12 week deployment', desc: 'Designed to run parallel to S/4HANA migrations. Factory-by-factory rollout with no disruption to production processes.' },
    ],
    mandates: ['e-Invoicing (B2B & B2G)', 'SAF-T', 'VAT Returns', 'EC Sales Lists', 'Intrastat', 'eTransport / eWaybill'],
    stats: [
      { value: '40+', label: 'Country mandates supported' },
      { value: '8-12', label: 'Weeks to go-live' },
      { value: '99.9%', label: 'Submission success rate' },
      { value: '1', label: 'Platform for everything' },
    ],
    caseStudy: 'A European steel and manufacturing group deployed Taxera across 8 countries during their S/4HANA migration — consolidating SAF-T, e-Invoicing, and statutory reporting into a single integration point.',
  },
  retail: {
    name: 'Retail & Consumer Goods',
    icon: ShoppingBag,
    color: '#06b6d4',
    hero: 'Scale your tax compliance as fast as you scale your stores.',
    subtitle: 'High-volume B2C invoicing, marketplace obligations, and rapid expansion into new markets demand a platform that keeps up — without adding headcount.',
    challenges: [
      { title: 'Massive B2C invoice volumes', desc: 'Millions of point-of-sale transactions per month, each potentially requiring a fiscalized eInvoice or eReceipt depending on the jurisdiction.' },
      { title: 'Marketplace VAT obligations', desc: 'Deemed supplier rules, marketplace reporting, and digital services tax across the EU, UK, and beyond.' },
      { title: 'Rapid geographic expansion', desc: 'Opening new markets means new mandates. Each country has different e-Invoicing formats, filing deadlines, and authority portals.' },
      { title: 'Promotional complexity', desc: 'Discounts, returns, credit notes, and multi-currency pricing create reconciliation challenges that compound across jurisdictions.' },
    ],
    taxeraValue: [
      { title: 'Built for volume', desc: 'Process millions of transactions monthly with real-time validation and submission — no batching delays, no overnight processing.' },
      { title: 'New market in weeks', desc: 'Pre-built country mandates mean you can add a new jurisdiction to your compliance stack in weeks, not months.' },
      { title: 'POS integration', desc: 'Native connectors for SAP Retail, Oracle Retail, and custom POS systems — ensuring every transaction is compliant at the point of sale.' },
      { title: 'Credit note automation', desc: 'Automated handling of returns, corrections, and credit notes across every jurisdiction — with full audit trail and matching.' },
    ],
    mandates: ['B2C e-Invoicing / eFiscalization', 'B2B e-Invoicing', 'SAF-T', 'VAT Returns', 'MOSS / OSS', 'Marketplace Reporting'],
    stats: [
      { value: 'M+', label: 'Monthly transactions handled' },
      { value: '2-4', label: 'Weeks to add new country' },
      { value: '100%', label: 'Real-time processing' },
      { value: '0', label: 'Manual interventions needed' },
    ],
    caseStudy: 'A global discount retailer processing millions of B2C invoices daily deployed Taxera across EMEA — handling compliance at scale without adding a single tax operations headcount.',
  },
  automotive: {
    name: 'Automotive',
    icon: Car,
    color: '#8b5cf6',
    hero: 'Keep your supply chain compliant at the speed your production line demands.',
    subtitle: 'Cross-border parts flows, just-in-time logistics, and eTransport mandates require real-time compliance that cannot slow down production.',
    challenges: [
      { title: 'Just-in-time supply chains', desc: 'Parts crossing borders multiple times before assembly — each movement triggering eTransport, customs, and VAT obligations in real-time.' },
      { title: 'eTransport mandates', desc: 'Romania eTransport, Turkey eWaybill, and emerging European eDelivery requirements demand real-time tracking of goods in transit.' },
      { title: 'Tier-1 / Tier-2 supplier complexity', desc: 'Thousands of suppliers across dozens of countries, each with different invoicing formats and compliance requirements.' },
      { title: 'After-sales & warranty', desc: 'Service networks, dealer billing, warranty claims, and recall-related credit notes each carry specific tax treatment by jurisdiction.' },
    ],
    taxeraValue: [
      { title: 'Real-time eTransport', desc: 'Automated generation and submission of eTransport documents as goods move between countries — no production delays.' },
      { title: 'Supplier portal integration', desc: 'Validate and process incoming supplier invoices against every country mandate — catch errors before they enter your ERP.' },
      { title: 'SAP Automotive integration', desc: 'Native support for SAP Automotive-specific processes: SD, MM, FI-CA — compliance embedded in your existing workflows.' },
      { title: 'Cross-border automation', desc: 'Automated handling of triangulation, chain transactions, and intra-community movements across your entire European production network.' },
    ],
    mandates: ['e-Invoicing (B2B)', 'eTransport / eWaybill', 'SAF-T', 'VAT Returns', 'Intrastat', 'EC Sales Lists'],
    stats: [
      { value: 'Real-time', label: 'eTransport compliance' },
      { value: '0', label: 'Production line delays' },
      { value: '100%', label: 'Supplier invoice validation' },
      { value: '30+', label: 'Countries covered' },
    ],
    caseStudy: 'A major European automotive OEM integrated Taxera across their production and logistics network — automating eTransport for Romania and e-Invoicing across 12 EU markets without touching their SAP SD configuration.',
  },
  healthcare: {
    name: 'Healthcare & Life Sciences',
    icon: HeartPulse,
    color: '#10b981',
    hero: 'Compliance-grade tax technology for regulated industries.',
    subtitle: 'In healthcare, every audit trail matters. Taxera delivers the transparency, traceability, and regulatory compliance that your industry demands.',
    challenges: [
      { title: 'Regulated audit requirements', desc: 'Healthcare tax compliance requires full traceability — every invoice, every correction, every submission must be audit-ready for both tax and industry regulators.' },
      { title: 'Complex pricing structures', desc: 'Transfer pricing, hospital billing, insurance reimbursements, and government contracts each carry specific VAT treatments across jurisdictions.' },
      { title: 'Multi-country clinical operations', desc: 'Clinical trials, manufacturing sites, and distribution networks span multiple regulatory jurisdictions with different compliance requirements.' },
      { title: 'Data sovereignty', desc: 'Patient-adjacent data must comply with GDPR and local data residency requirements — tax systems that process this data must be equally rigorous.' },
    ],
    taxeraValue: [
      { title: 'Complete audit trail', desc: 'Every transaction is logged, timestamped, and traceable — from ERP entry to tax authority acknowledgment. Built for the most rigorous audits.' },
      { title: 'ISO 27001 certified', desc: 'Enterprise-grade information security that meets the standards healthcare organizations require for their technology partners.' },
      { title: 'GDPR-compliant processing', desc: 'Data processed and stored in compliance with EU data protection regulations — no cross-border data transfers without proper safeguards.' },
      { title: 'Regulatory alignment', desc: 'Tax compliance that understands regulated industries — proper handling of exempt supplies, reduced rates, and sector-specific treatments.' },
    ],
    mandates: ['e-Invoicing (B2B & B2G)', 'SAF-T', 'VAT Returns', 'Exemption Management', 'Transfer Pricing Documentation'],
    stats: [
      { value: 'ISO 27001', label: 'Certified platform' },
      { value: 'GDPR', label: 'Compliant processing' },
      { value: '100%', label: 'Audit trail coverage' },
      { value: '24/7', label: 'Monitoring & alerting' },
    ],
    caseStudy: 'A global pharmaceutical company deployed Taxera to unify e-Invoicing and SAF-T across 15 countries — meeting both tax authority and FDA audit requirements with a single compliance platform.',
  },
  publicSector: {
    name: 'Public Sector',
    icon: Building2,
    color: '#f59e0b',
    hero: 'Transparent tax compliance for government mandates and public procurement.',
    subtitle: 'Government e-Invoicing mandates (B2G) require specific format compliance, portal submissions, and full transparency. Taxera handles it natively.',
    challenges: [
      { title: 'B2G mandates', desc: 'Selling to government entities requires B2G e-Invoicing in specific formats — Peppol BIS, FatturaPA, Factur-X — with strict validation and submission requirements.' },
      { title: 'Public procurement rules', desc: 'Each EU member state has specific e-Invoicing requirements for public procurement, with different portals and validation schemas.' },
      { title: 'Transparency requirements', desc: 'Government-adjacent organizations face heightened scrutiny — every tax submission must be defensible, documented, and accessible.' },
      { title: 'Budget cycle compliance', desc: 'Annual budget cycles and government reporting periods create specific filing cadences that differ from commercial timelines.' },
    ],
    taxeraValue: [
      { title: 'B2G format compliance', desc: 'Native support for every B2G e-Invoicing format: Peppol BIS 3.0, Italian FatturaPA, French Chorus Pro, German XRechnung, and more.' },
      { title: 'Portal submission automation', desc: 'Direct submission to every government portal — no manual uploads, no format conversion, no human intervention required.' },
      { title: 'Full transparency', desc: 'Complete visibility into every submission, validation result, and authority response — audit-ready at all times.' },
      { title: 'Multi-format support', desc: 'One platform that handles both B2G and B2B compliance — no separate systems for government and commercial invoicing.' },
    ],
    mandates: ['B2G e-Invoicing (Peppol)', 'B2B e-Invoicing', 'SAF-T', 'VAT Returns', 'Public Procurement Reporting'],
    stats: [
      { value: '27', label: 'EU B2G formats supported' },
      { value: '100%', label: 'Automated portal submission' },
      { value: 'Peppol', label: 'Native compliance' },
      { value: '0', label: 'Manual uploads needed' },
    ],
    caseStudy: 'A European public infrastructure organization deployed Taxera to automate B2G e-Invoicing across 6 EU member states — achieving 100% compliance with Peppol BIS requirements from day one.',
  },
  logistics: {
    name: 'Logistics & Supply Chain',
    icon: Truck,
    color: '#14b8a6',
    hero: 'Real-time compliance for goods that never stop moving.',
    subtitle: 'eTransport mandates, cross-border waybills, and continuous movement of goods require tax compliance that operates at the speed of your logistics network.',
    challenges: [
      { title: 'eTransport mandates', desc: 'Romania, Turkey, and emerging EU markets require real-time electronic documentation for goods in transit — with strict penalties for non-compliance.' },
      { title: 'Cross-border movements', desc: 'Goods crossing multiple borders daily — each movement triggering different customs, VAT, and documentation requirements.' },
      { title: 'Carrier / 3PL coordination', desc: 'Third-party logistics providers, carriers, and freight forwarders each generate compliance obligations that must be tracked and reported.' },
      { title: 'Warehouse compliance', desc: 'Bonded warehouses, call-off stock, and consignment arrangements each carry specific VAT treatments depending on the jurisdiction.' },
    ],
    taxeraValue: [
      { title: 'Real-time eTransport', desc: 'Automated generation and submission of eTransport, eWaybill, and eCMR documents as goods move — integrated directly with your TMS.' },
      { title: 'Cross-border automation', desc: 'Automatic determination of VAT treatment for triangulation, chain transactions, and multi-stop shipments.' },
      { title: 'TMS integration', desc: 'Native integration with logistics management systems — compliance data flows automatically from shipment creation to authority submission.' },
      { title: 'Call-off stock management', desc: 'Automated tracking and reporting of call-off stock arrangements under the EU simplification directive.' },
    ],
    mandates: ['eTransport / eWaybill', 'eCMR', 'e-Invoicing', 'SAF-T', 'VAT Returns', 'Intrastat', 'Customs'],
    stats: [
      { value: 'Real-time', label: 'Document generation' },
      { value: '0', label: 'Shipment delays from compliance' },
      { value: '100%', label: 'eTransport coverage' },
      { value: '24/7', label: 'Cross-border monitoring' },
    ],
    caseStudy: 'A pan-European logistics provider integrated Taxera with their TMS to automate eTransport for Romania and eWaybill for Turkey — eliminating manual document preparation and reducing border delays to zero.',
  },
  energy: {
    name: 'Gas, Oil & Utilities',
    icon: Flame,
    color: '#ef4444',
    hero: 'Tax compliance for the energy sector\'s unique regulatory landscape.',
    subtitle: 'Energy taxes, excise duties, and sector-specific reporting obligations require specialized compliance capabilities that generic platforms cannot provide.',
    challenges: [
      { title: 'Energy-specific taxes', desc: 'Electricity tax, gas levies, carbon pricing, and renewable energy surcharges create a complex tax landscape beyond standard VAT.' },
      { title: 'Excise duty management', desc: 'Excise duties on fuels, lubricants, and energy products vary by jurisdiction and require separate reporting and payment workflows.' },
      { title: 'Regulated pricing', desc: 'Government-regulated tariffs, subsidized pricing, and social tariffs create complex invoicing requirements with specific VAT treatments.' },
      { title: 'Infrastructure investments', desc: 'Capital-intensive projects spanning multiple jurisdictions create reverse charge, import VAT, and construction-specific compliance requirements.' },
    ],
    taxeraValue: [
      { title: 'Sector-specific tax handling', desc: 'Native support for energy taxes, excise duties, and sector-specific VAT treatments — not generic workarounds.' },
      { title: 'Regulated market compliance', desc: 'Proper handling of regulated pricing, social tariffs, and government-mandated invoicing formats for energy distribution.' },
      { title: 'SAP IS-U integration', desc: 'Integration with SAP Industry Solution for Utilities — compliance embedded in your sector-specific ERP configuration.' },
      { title: 'Multi-tax reporting', desc: 'Unified platform for VAT, excise, energy tax, and environmental levy reporting — one system for all your tax obligations.' },
    ],
    mandates: ['e-Invoicing', 'SAF-T', 'VAT Returns', 'Excise Returns', 'Energy Tax Returns', 'Environmental Levies'],
    stats: [
      { value: 'Multi-tax', label: 'VAT + Excise + Energy' },
      { value: 'SAP IS-U', label: 'Native integration' },
      { value: '100%', label: 'Regulatory coverage' },
      { value: '1', label: 'Platform for all taxes' },
    ],
    caseStudy: 'A European energy utility deployed Taxera to consolidate VAT, excise duty, and energy tax reporting across 5 countries — replacing 3 separate systems with one platform.',
  },
  finance: {
    name: 'Finance & Insurance',
    icon: Shield,
    color: '#3b82f6',
    hero: 'Precision tax compliance for financial services and insurance.',
    subtitle: 'Exempt and partially exempt supplies, IPT reporting, and complex input tax recovery rules require a platform that understands financial services taxation.',
    challenges: [
      { title: 'VAT exemption complexity', desc: 'Financial services face partial exemption regimes — determining recoverable input VAT requires sophisticated apportionment calculations by jurisdiction.' },
      { title: 'Insurance Premium Tax (IPT)', desc: 'IPT rates, rules, and reporting vary by country and product type — requiring parallel compliance alongside VAT obligations.' },
      { title: 'Cross-border services', desc: 'B2B and B2C financial services across borders trigger complex place-of-supply rules, reverse charge mechanisms, and withholding requirements.' },
      { title: 'Regulatory reporting overlap', desc: 'Tax compliance intersects with financial regulatory reporting (IFRS, Solvency II) — requiring consistency across reporting frameworks.' },
    ],
    taxeraValue: [
      { title: 'Partial exemption handling', desc: 'Sophisticated input tax apportionment across exempt and taxable supplies — automated calculation by jurisdiction and business line.' },
      { title: 'IPT compliance', desc: 'Native support for Insurance Premium Tax reporting across European markets — rates, rules, and filing integrated into one platform.' },
      { title: 'Financial services expertise', desc: 'Tax treatment rules specifically configured for banking, insurance, asset management, and fintech business models.' },
      { title: 'Regulatory consistency', desc: 'Tax data aligned with financial regulatory reporting requirements — one source of truth for audit, compliance, and financial reporting.' },
    ],
    mandates: ['e-Invoicing', 'SAF-T', 'VAT Returns', 'IPT Returns', 'Withholding Tax', 'DAC7 Reporting'],
    stats: [
      { value: 'Automated', label: 'Partial exemption calc' },
      { value: 'IPT', label: 'Pan-European coverage' },
      { value: '100%', label: 'Audit readiness' },
      { value: 'DAC7', label: 'Platform reporting' },
    ],
    caseStudy: 'A pan-European insurance group deployed Taxera to automate IPT and VAT compliance across 12 markets — replacing manual spreadsheet-based apportionment with automated, auditable calculations.',
  },
  food: {
    name: 'Food & Beverages',
    icon: UtensilsCrossed,
    color: '#22c55e',
    hero: 'Keep your supply chain compliant from farm to shelf.',
    subtitle: 'Perishable goods, cross-border trade, and varying reduced VAT rates for food products demand specialized compliance that understands your industry.',
    challenges: [
      { title: 'Reduced VAT rate complexity', desc: 'Food products often qualify for reduced or zero VAT rates — but the rules vary wildly by country, product category, and preparation method.' },
      { title: 'Cross-border perishable trade', desc: 'Time-sensitive shipments of perishable goods require immediate compliance documentation — delays at borders are not an option.' },
      { title: 'Retail & wholesale channels', desc: 'Selling to both retailers and directly to consumers creates dual B2B and B2C compliance obligations across channels.' },
      { title: 'Agricultural input VAT', desc: 'Flat-rate farmer schemes, agricultural exemptions, and input VAT recovery on farming supplies vary by EU member state.' },
    ],
    taxeraValue: [
      { title: 'Reduced rate automation', desc: 'Automatic determination of correct VAT rate by product category and destination country — no manual rate lookups or classification errors.' },
      { title: 'Perishable goods compliance', desc: 'Real-time eTransport and e-Invoicing for time-sensitive shipments — compliance documents generated before goods leave the warehouse.' },
      { title: 'Multi-channel support', desc: 'Unified compliance for B2B wholesale, B2C retail, D2C e-commerce, and marketplace channels — all from one platform.' },
      { title: 'Farm-to-shelf traceability', desc: 'Complete compliance trail from agricultural input through processing to retail sale — supporting both tax and food safety audits.' },
    ],
    mandates: ['e-Invoicing', 'eTransport', 'SAF-T', 'VAT Returns', 'EC Sales Lists', 'Intrastat'],
    stats: [
      { value: 'Auto', label: 'Reduced rate determination' },
      { value: '0', label: 'Border delays' },
      { value: 'Multi', label: 'Channel compliance' },
      { value: '100%', label: 'Traceability' },
    ],
    caseStudy: 'A global FMCG company deployed Taxera across 14 EMEA markets — automating reduced rate determination for thousands of food SKUs and eliminating manual e-Invoicing across their retail distribution network.',
  },
  travel: {
    name: 'Travel & Technology',
    icon: Plane,
    color: '#6366f1',
    hero: 'Tax compliance for digital services and global travel platforms.',
    subtitle: 'Digital services tax, marketplace rules, and complex B2C VAT across 50+ jurisdictions — Taxera handles the compliance so you can focus on growth.',
    challenges: [
      { title: 'Digital services VAT', desc: 'B2C digital services trigger VAT obligations in the customer\'s country — requiring real-time determination and collection across 100+ jurisdictions.' },
      { title: 'Marketplace obligations', desc: 'Deemed supplier rules, split payments, and marketplace reporting requirements vary by country and platform model.' },
      { title: 'Multi-currency complexity', desc: 'Transactions in dozens of currencies with different exchange rate rules, rounding requirements, and denomination obligations.' },
      { title: 'Global booking flows', desc: 'Airline tickets, hotel bookings, and travel packages each carry different VAT treatments depending on origin, destination, and supplier location.' },
    ],
    taxeraValue: [
      { title: 'OSS/IOSS compliance', desc: 'Native support for the EU One Stop Shop and Import One Stop Shop — simplifying cross-border B2C VAT for digital services and low-value goods.' },
      { title: 'Real-time rate determination', desc: 'Automatic VAT rate lookup by customer location, service type, and applicable scheme — embedded in your booking flow.' },
      { title: 'Marketplace reporting', desc: 'Automated generation of marketplace reports, deemed supplier documentation, and split payment records across all applicable jurisdictions.' },
      { title: 'Travel-specific rules', desc: 'Native handling of TOMS (Tour Operators Margin Scheme), airline ticket taxation, and hotel booking VAT across global markets.' },
    ],
    mandates: ['B2C e-Invoicing', 'OSS / IOSS', 'Digital Services Tax', 'VAT Returns', 'Marketplace Reporting', 'TOMS'],
    stats: [
      { value: '50+', label: 'Jurisdictions covered' },
      { value: 'OSS', label: 'Native compliance' },
      { value: 'Real-time', label: 'Rate determination' },
      { value: '0', label: 'Manual interventions' },
    ],
    caseStudy: 'A global travel technology provider deployed Taxera to automate multi-country digital services VAT — handling OSS compliance and real-time rate determination for millions of monthly bookings.',
  },
  fashion: {
    name: 'Fashion & Lifestyle',
    icon: Gem,
    color: '#ec4899',
    hero: 'Scale your brand globally without the compliance headaches.',
    subtitle: 'D2C expansion, marketplace sales, and cross-border returns require a compliance platform that moves as fast as fashion cycles.',
    challenges: [
      { title: 'D2C cross-border sales', desc: 'Selling directly to consumers across borders triggers VAT registration, collection, and reporting obligations in every destination market.' },
      { title: 'Returns & refunds', desc: 'Fashion has the highest return rates in e-commerce — each return requires proper VAT adjustment, credit note, and reconciliation.' },
      { title: 'Import duties', desc: 'Sourcing from Asia and manufacturing in multiple countries creates complex customs, import VAT, and duty obligations.' },
      { title: 'Marketplace proliferation', desc: 'Selling on multiple marketplaces (Amazon, Zalando, ASOS) creates parallel compliance obligations with deemed supplier rules.' },
    ],
    taxeraValue: [
      { title: 'IOSS compliance', desc: 'Native support for Import One Stop Shop — simplifying import VAT for low-value consignments shipped directly to EU consumers.' },
      { title: 'Returns automation', desc: 'Automated credit note generation, VAT adjustment, and reconciliation for returns — across every marketplace and channel.' },
      { title: 'Multi-marketplace support', desc: 'Unified compliance for Amazon, Zalando, ASOS, and your own D2C store — one platform for all channels.' },
      { title: 'Rapid market entry', desc: 'Pre-built country mandates let you launch in new markets in weeks — compliance ready before your first order ships.' },
    ],
    mandates: ['B2C e-Invoicing', 'IOSS', 'OSS', 'VAT Returns', 'Marketplace Reporting', 'Customs / Duties'],
    stats: [
      { value: 'IOSS', label: 'Native compliance' },
      { value: 'Auto', label: 'Returns handling' },
      { value: 'Multi', label: 'Marketplace support' },
      { value: '2-4 wk', label: 'New market launch' },
    ],
    caseStudy: 'A European fashion brand expanded D2C sales to 20 EU markets using Taxera — automating IOSS compliance and returns processing across their own webshop and 4 marketplace channels.',
  },
  construction: {
    name: 'Construction',
    icon: HardHat,
    color: '#f97316',
    hero: 'Tax compliance for complex project-based operations.',
    subtitle: 'Reverse charge mechanisms, subcontractor reporting, and multi-year project accounting require specialized compliance that understands construction.',
    challenges: [
      { title: 'Reverse charge complexity', desc: 'Construction services trigger domestic reverse charge in many EU markets — with different thresholds, definitions, and exceptions by country.' },
      { title: 'Subcontractor chains', desc: 'Deep subcontractor chains create CIS (Construction Industry Scheme) obligations and require proper invoice handling at every tier.' },
      { title: 'Cross-border projects', desc: 'International construction projects create permanent establishment risks, withholding tax obligations, and foreign VAT registration requirements.' },
      { title: 'Long-term contracts', desc: 'Multi-year projects with stage payments, retention, and milestone billing each carry specific VAT point-of-taxation rules.' },
    ],
    taxeraValue: [
      { title: 'Reverse charge automation', desc: 'Automatic determination and application of domestic reverse charge rules by country, service type, and contractor classification.' },
      { title: 'Subcontractor compliance', desc: 'Automated CIS deduction, reporting, and certificate management — integrated with your project accounting system.' },
      { title: 'Project-based tracking', desc: 'Compliance organized by project, not just by entity — giving project managers visibility into tax obligations at the project level.' },
      { title: 'Cross-border PE management', desc: 'Monitoring and alerting for permanent establishment thresholds — proactive notifications before obligations are triggered.' },
    ],
    mandates: ['e-Invoicing', 'SAF-T', 'VAT Returns', 'Reverse Charge', 'CIS Reporting', 'Withholding Tax'],
    stats: [
      { value: 'Auto', label: 'Reverse charge handling' },
      { value: 'CIS', label: 'Automated reporting' },
      { value: 'Project', label: 'Level compliance view' },
      { value: 'PE', label: 'Threshold monitoring' },
    ],
    caseStudy: 'A European construction group deployed Taxera to automate reverse charge compliance across 9 EU markets — eliminating manual determination errors and reducing audit findings by 90%.',
  },
};

const IndustryPage = () => {
  const { sector } = useParams();

  const sectorKey = sector === 'public-sector' ? 'publicSector' : sector;
  const data = INDUSTRY_DATA[sectorKey];
  // Memoise the "other industries" cross-link list — Object.entries → filter → slice
  // runs on every render otherwise.
  const otherIndustries = useMemo(
    () =>
      Object.entries(INDUSTRY_DATA)
        .filter(([key]) => key !== sectorKey)
        .map(([key, ind]) => [key === 'publicSector' ? 'public-sector' : key, ind])
        .slice(0, 6),
    [sectorKey]
  );

  // 301-equivalent: redirect old publicSector URL to public-sector
  if (sector === 'publicSector') {
    return <Navigate to="/industries/public-sector" replace />;
  }

  if (!data) {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-taxera-charcoal mb-4">Industry not found</h1>
            <Link to="/" className="text-taxera-blue hover:underline">Return to homepage</Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  const Icon = data.icon;

  return (
    <PageLayout>
      {/* Hero */}
      <section className="relative bg-slate-900 overflow-hidden -mt-4" data-testid="industry-hero">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-[120px]" style={{ backgroundColor: `${data.color}08` }} />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full blur-[100px]" style={{ backgroundColor: `${data.color}06` }} />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 pt-16 pb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <div className="inline-flex items-center gap-2.5 px-5 py-2 bg-white/[0.06] border border-white/[0.08] rounded-full mb-8">
              <Icon className="w-4 h-4" style={{ color: data.color }} strokeWidth={1.5} />
              <span className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-400">{data.name}</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-testid="industry-title">
              {data.hero}
            </h1>
            <p className="text-base lg:text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto mb-8">
              {data.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-xl transition-all hover:scale-105"
                data-testid="industry-cta-demo"
              >
                Talk to a {data.name} specialist
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/evaluate"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white border border-white/20 rounded-full font-semibold hover:bg-white/20 transition-all"
                data-testid="industry-cta-evaluate"
              >
                Take the Diagnostic
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {data.stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.08 }}
                className="text-center"
              >
                <div className="font-heading text-2xl font-bold" style={{ color: data.color }}>{stat.value}</div>
                <div className="text-xs text-slate-400 mt-1 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="py-16 lg:py-20 bg-slate-50" data-testid="industry-challenges">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-xs font-bold tracking-[0.25em] text-slate-500 uppercase mb-3">Industry challenges</div>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal">
              What makes {data.name.toLowerCase()} tax compliance hard
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.challenges.map((ch, i) => (
              <motion.div
                key={ch.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-amber-200 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-2">{ch.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{ch.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Taxera for this industry */}
      <section className="py-16 lg:py-20 bg-white" data-testid="industry-value">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-xs font-bold tracking-[0.25em] uppercase mb-3" style={{ color: data.color }}>Why Taxera</div>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal">
              How Taxera solves {data.name.toLowerCase()} compliance
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.taxeraValue.map((val, i) => (
              <motion.div
                key={val.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                style={{ borderLeftWidth: 3, borderLeftColor: data.color }}
              >
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: data.color }} strokeWidth={1.5} />
                  <div>
                    <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-2">{val.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{val.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Relevant mandates */}
      <section className="py-16 lg:py-20 bg-slate-50" data-testid="industry-mandates">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal mb-3">
              Relevant compliance mandates
            </h2>
            <p className="text-sm text-slate-500">The tax and reporting obligations most critical for {data.name.toLowerCase()} companies.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {data.mandates.map((m) => (
              <span key={m} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-taxera-charcoal">
                <FileText className="w-3.5 h-3.5 text-taxera-teal" strokeWidth={1.5} />
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Case study */}
      <section className="py-16 lg:py-20 bg-slate-900" data-testid="industry-case-study">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-xs font-bold tracking-[0.25em] text-taxera-teal uppercase mb-4">In practice</div>
          <blockquote className="font-heading text-xl lg:text-2xl text-white leading-relaxed mb-8">
            "{data.caseStudy}"
          </blockquote>
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-xl transition-all hover:scale-105"
            data-testid="industry-case-study-cta"
          >
            See how we can help your {data.name.toLowerCase()} operations
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Cross-sell: Other industries */}
      <section className="py-16 lg:py-20 bg-white" data-testid="industry-cross-sell">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-xl font-bold text-taxera-charcoal">Explore other industries</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {otherIndustries
              .map(([key, ind]) => {
                const IndIcon = ind.icon;
                return (
                  <Link
                    key={key}
                    to={`/industries/${key}`}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-full text-sm font-medium text-taxera-charcoal hover:border-taxera-teal/40 hover:bg-white transition-all"
                  >
                    <IndIcon className="w-4 h-4" style={{ color: ind.color }} strokeWidth={1.5} />
                    {ind.name}
                  </Link>
                );
              })}
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default IndustryPage;
